import React, { useState, useEffect } from 'react';
import { Building2, ArrowLeft, MapPin, Phone, Clock, Save, AlertCircle, DollarSign, MessageSquare, Megaphone } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI, handleApiError } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { FormSelect } from '../components/FormSelect';

const NewLocationForm = () => {
  const { isSystemAdmin } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    type: 'both', // valet, wash, both
    address: '',
    city: '',
    district: '',
    phone: '',
    default_valet_price: 0,
    sms_enabled: true,
    ad_enabled: true,
    working_hours: {
      monday: { open: '08:00', close: '22:00' },
      tuesday: { open: '08:00', close: '22:00' },
      wednesday: { open: '08:00', close: '22:00' },
      thursday: { open: '08:00', close: '22:00' },
      friday: { open: '08:00', close: '22:00' },
      saturday: { open: '08:00', close: '22:00' },
      sunday: { open: '09:00', close: '21:00' }
    },
    is_active: true
  });

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const cleanValue = value.replace(/\D/g, '');

    // Apply Turkish phone number format: 0XXX XXX XX XX
    if (cleanValue.length === 0) return '';
    if (cleanValue.length <= 1) return cleanValue;
    if (cleanValue.length <= 4) return `${cleanValue.slice(0, 4)}`;
    if (cleanValue.length <= 7) return `${cleanValue.slice(0, 4)} ${cleanValue.slice(4)}`;
    if (cleanValue.length <= 9) return `${cleanValue.slice(0, 4)} ${cleanValue.slice(4, 7)} ${cleanValue.slice(7)}`;
    return `${cleanValue.slice(0, 4)} ${cleanValue.slice(4, 7)} ${cleanValue.slice(7, 9)} ${cleanValue.slice(9, 11)}`;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'phone') {
      const formattedValue = formatPhoneNumber(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleWorkingHoursChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      working_hours: {
        ...prev.working_hours,
        [day]: {
          ...prev.working_hours[day],
          [field]: value
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const submitData = { ...formData };

      // Clean phone number by removing formatting
      if (submitData.phone) {
        submitData.phone = submitData.phone.replace(/\D/g, '');
      }

      const response = await adminAPI.createLocation(submitData);

      if (response.data.success) {
        setSuccess('Lokasyon başarıyla oluşturuldu!');
        setTimeout(() => {
          navigate('/admin/locations');
        }, 2000);
      } else {
        setError(response.data.message || 'Lokasyon oluşturulurken hata oluştu');
      }
    } catch (err) {
      console.error('Failed to create location:', err);
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const dayNames = {
    monday: 'Pazartesi',
    tuesday: 'Salı',
    wednesday: 'Çarşamba',
    thursday: 'Perşembe',
    friday: 'Cuma',
    saturday: 'Cumartesi',
    sunday: 'Pazar'
  };

  // Günlerin doğru sırası
  const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  if (!isSystemAdmin()) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
          <p className="text-red-700">Bu sayfaya erişim yetkiniz yok.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/admin/locations"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Building2 className="w-7 h-7 mr-3 text-[#7a2c2c]" />
              Yeni Lokasyon Oluştur
            </h1>
            <p className="text-gray-600">Sisteme yeni bir lokasyon ekleyin</p>
          </div>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <Building2 className="h-5 w-5 text-green-400 mr-3" />
            <p className="text-green-700">{success}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Temel Bilgiler</h3>
          </div>
          <div className="card-body space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">
                  Lokasyon Adı *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Örn: Merkez Vale"
                  required
                />
              </div>
              <div>
                <FormSelect
                  name="type"
                  label="Lokasyon Tipi"
                  value={formData.type}
                  onChange={handleInputChange}
                  options={[
                    { value: 'both', label: 'Vale + Oto Yıkama' },
                    { value: 'valet', label: 'Vale' },
                    { value: 'wash', label: 'Oto Yıkama' }
                  ]}
                  required
                />
              </div>
            </div>

            <div>
              <label className="form-label">
                <MapPin className="w-4 h-4 inline mr-2" />
                Adres *
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className="form-input"
                rows={3}
                placeholder="Tam adres bilgisini giriniz"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="form-label">
                  Şehir *
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Örn: İstanbul"
                  required
                />
              </div>
              <div>
                <label className="form-label">
                  İlçe *
                </label>
                <input
                  type="text"
                  name="district"
                  value={formData.district}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Örn: Şişli"
                  required
                />
              </div>
              <div>
                <label className="form-label">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Telefon *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="0212 123 45 67"
                  maxLength="14"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">
                  Default Vale Fiyatı (₺)
                </label>
                <input
                  type="number"
                  name="default_valet_price"
                  value={formData.default_valet_price}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
                <p className="text-xs text-gray-500 mt-1">Lokasyon için varsayılan vale hizmeti fiyatı</p>
              </div>
              <div>
                <label className="form-label">
                  <MessageSquare className="w-4 h-4 inline mr-2" />
                  SMS Gönderimi
                </label>
                <div className="flex items-center mt-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="sms_enabled"
                      checked={formData.sms_enabled}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className={`relative w-11 h-6 rounded-full transition-colors ${formData.sms_enabled ? 'bg-green-500' : 'bg-gray-300'}`}>
                      <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${formData.sms_enabled ? 'translate-x-5' : ''}`}></div>
                    </div>
                    <span className="ml-3 block text-sm text-gray-900">
                      SMS bildirimleri {formData.sms_enabled ? 'aktif' : 'pasif'}
                    </span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">Müşterilere SMS bildirimleri gönderilsin mi?</p>
              </div>

              {/* Ad Enabled Toggle */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block flex items-center gap-2">
                  <Megaphone className="w-4 h-4" /> Reklam Gösterimi
                </label>
                <div className="flex items-center">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="ad_enabled"
                      checked={formData.ad_enabled}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className={`relative w-11 h-6 rounded-full transition-colors ${formData.ad_enabled ? 'bg-orange-500' : 'bg-gray-300'}`}>
                      <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${formData.ad_enabled ? 'translate-x-5' : ''}`}></div>
                    </div>
                    <span className="ml-3 block text-sm text-gray-900">
                      Reklamlar {formData.ad_enabled ? 'gösteriliyor' : 'gizleniyor'}
                    </span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">Müşteri sayfalarında reklam gösterilsin mi? (Ücretli paket için kapatılabilir)</p>
              </div>
            </div>

            <div className="flex items-center">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="sr-only"
                />
                <div className={`relative w-11 h-6 rounded-full transition-colors ${formData.is_active ? 'bg-green-500' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${formData.is_active ? 'translate-x-5' : ''}`}></div>
                </div>
                <span className="ml-3 block text-sm text-gray-900">
                  Lokasyonu {formData.is_active ? 'aktif' : 'pasif'} olarak oluştur
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Working Hours */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Clock className="w-5 h-5 mr-2" />
              Çalışma Saatleri
            </h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
              {dayOrder.map((day) => (
                <div key={day} className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 block">
                    {dayNames[day]}
                  </label>
                  <div className="space-y-2">
                    <input
                      type="time"
                      value={formData.working_hours[day].open}
                      onChange={(e) => handleWorkingHoursChange(day, 'open', e.target.value)}
                      className="w-full text-xs px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-[#7a2c2c] focus:border-[#7a2c2c]"
                    />
                    <input
                      type="time"
                      value={formData.working_hours[day].close}
                      onChange={(e) => handleWorkingHoursChange(day, 'close', e.target.value)}
                      className="w-full text-xs px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-[#7a2c2c] focus:border-[#7a2c2c]"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <Link
            to="/admin/locations"
            className="btn-secondary"
          >
            İptal
          </Link>
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary flex items-center"
          >
            {isLoading ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Oluşturuluyor...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Lokasyon Oluştur
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewLocationForm;